/**
 * Material-UI styles hooks for the Keywords component.
 *
 * @module components/sim/keywords/Keywords
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Defines a set of reusable styles for a toolbar component, using Material-UI's styling solution.
 *
 * The returned style object contains the following CSS rules:
 *
 * - `root`: Styles for the root container of the toolbar. Includes padding for left and right sides,
 *   based on the spacing unit of the theme.
 * - `title`: Styles for the toolbar title element. Includes properties to make the title flexible
 *   for layout purposes and apply bold font weight.
 * - `icon`: Styles for toolbar icons. Adds padding, a rounded background with a specific color, and margin
 *   around the icon.
 * - `saveBtn`: Styles for a save button within the toolbar. Specifies padding for all sides.
 * - `toggleBtn`: Styles for a toggle button group in the toolbar. Includes height specification, margin-left
 *   for spacing, and specific color options for selected and unselected toggle buttons.
 *
 * The styles are created using Material-UI's makeStyles utility and are responsive to the theme's configuration.
 */
export const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    fontWeight: 'bold',
  },
  icon: {
    padding: '6px',
    backgroundColor: '#F5E2E9',
    borderRadius: '11%',
    margin: theme.spacing(0, 1),
  },
  saveBtn: {
    padding: '8px 40px 8px 40px',
  },
  toggleBtn: {
    height: 38,
    '& .MuiToggleButton-root.Mui-selected': {
      backgroundColor: '#F5E2E9',
      color: '#560020',
    },
    '& .MuiToggleButton-root': {
      color: '#626161',
    },
    marginLeft: 8,
  },
}));

/**
 * A hook that generates JSS styles for customizing the appearance of a navigation bar using Material-UI's `makeStyles`.
 *
 * The object returned by the hook contains styles for the following CSS classes:
 *
 * - `navbar`: Defines the styles for the overall navigation bar container, including padding, background color, width, height,
 *   margin, and border radius.
 * - `tab`: Defines the styles for tabs within the navigation bar, including padding, dimensions, font size, minimum height,
 *   and text transformation.
 * - `tabPanel`: Defines styles for the content displayed when a tab is selected, including padding and minimum height.
 */
export const useNavbarStyles = makeStyles((theme) => ({
  navbar: {
    padding: theme.spacing(1, 1, 1, 1),
    backgroundColor: '#3E5785',
    width: '99%',
    height: '46px',
    margin: theme.spacing(0, 1),
    borderRadius: '5px',
  },
  tab: {
    padding: theme.spacing(0),
    minWidth: '125px',
    width: '120px',
    fontSize: '11px',
    minHeight: '30px',
    height: '30px',
    textTransform: 'none',
  },
  tabPanel: {
    padding: '14px',
    minHeight: '26rem',
  },
}));

/**
 * useTabContentStyles is a hook generated by the makeStyles utility from Material-UI.
 * It defines custom CSS-in-JS styles for various components within a tab content section.
 *
 * Properties:
 * - root: Applies a top margin to the root container of the tab content.
 * - accordionDetails: Adds padding around elements inside the accordion details section.
 * - arrowDropDown: Styles the dropdown arrow with a background color, margins, and rounded corners.
 * - title: Sets the background color, padding, and border radius for the title container.
 */
export const useTabContentStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  accordionDetails: {
    padding: theme.spacing(1, 1, 1, 1),
  },
  arrowDropDown: {
    backgroundColor: '#D7D7D7',
    margin: '3px 9px -5px 5px',
    borderRadius: '3px',
  },
  title: {
    backgroundColor: '#EFEFEF',
    padding: '6px 6px',
    borderRadius: '3px',
  },
}));
